import { groupBy, splitClusterResponse } from "common/api";

const unitScale = (unit) =>
  unit === "G"
    ? 1
    : unit === "T"
    ? 1024
    : unit === "M"
    ? Math.pow(2, -10)
    : unit === "k"
    ? Math.pow(2, -20)
    : 0;

const parseVolume = (input, defaultUnit = "M") => {
  const matches = input.match(/(\d+\.?\d*)([MGT])?/);
  if (matches === null) {
    return null;
  }
  const [, value, unit] = matches;
  return parseFloat(value) * unitScale(unit || defaultUnit);
};

const parseKeys = (head, translations = {}) =>
  head.split(/\s+/).map((key) => translations[key] || key.toLowerCase());

export const parseSystemMemoryUsage = (input) => {
  const [header, , buffersRow] = input.trim().split("\n");
  const headerAttrs = header.split(/\s+/);
  const indexAttrMap = headerAttrs.reduce((acc, attr, index) => ({
    ...acc,
    [attr]: index + 1,
  }), {});
  const values = buffersRow.trim().split(/\s+/)
  const freeMemory = values[indexAttrMap["free"]];
  const usedMemory = values[indexAttrMap["used"]];

  return {
    used: parseVolume(usedMemory, "k"),
    free: parseVolume(freeMemory, "k"),
  };
};

// The response of a BQN asked with the "bqn-id ID" option carries no BQN ID,
// so the BQN of the data is the one selected in the header.
export const parseSystemMemoryCluster = (input, mode, bqnIdSel) => {
  return mode === "standalone"
    ? parseSystemMemoryUsage(input)
    : splitClusterResponse(input).map(({ bqnId, resp }) => ({
        bqnId: bqnId ?? bqnIdSel,
        ...parseSystemMemoryUsage(resp),
      }));
};
  

const parseFields =
  (keys, skipped = [], unit = undefined) =>
  (line) =>
    Object.fromEntries(
      line
        .trim(" ")
        .split(/\s+/)
        .flatMap((value, index) =>
          skipped.includes(keys[index])
            ? []
            : [
                [
                  keys[index],
                  unit === undefined
                    ? parseFloat(value)
                    : parseVolume(value, unit),
                ],
              ]
        )
    );

const sumUpPools = ({ size = 0, allocated = 0, used = 0 }, row) => ({
  size: size + row.size,
  allocated: allocated + row.allocated,
  used: used + row.used,
  bqn: row.bqn,
});

export const fixedPrecision = (value, decimals = 4) =>
  parseFloat(value.toFixed(decimals));

const calcFreeAndUsed = ({ size = 0, allocated = 0, used = 0 }) => {
  used *= size / allocated / 1024 / 1024;
  const total = size / 1024 / 1024;
  const free = total - used;
  return { used: fixedPrecision(used), free: fixedPrecision(free) };
};

export const parsePoolMemoryUsage = (input, mode) => {
  const lines = input.length === 0 ? [] : input.trim("\n").split("\n");
  if (lines.length === 0) {
    return null;
  }
  const [head, ...rows] = lines;
  const translations = {
    POOLSIZEKB: "size",
    CHUNKALLOC: "allocated",
    CHUNKUSED: "used",
    bqn: "bqn",
  };

  const keys = parseKeys(head.trim(" "), translations);
  const skipped = keys.filter(
    (key) => Object.values(translations).includes(key) === false
  );

  if (mode === "standalone") {
    return calcFreeAndUsed(
      rows.map(parseFields(keys, skipped)).reduce(sumUpPools, {})
    );
  }

  const perBqn = rows.map(parseFields(keys, skipped)).reduce((acc, row) => {
    const { bqn, size, allocated, used } = row;
    if (!acc[bqn]) {
      acc[bqn] = [{ size, allocated, used }];
    } else {
      acc[bqn].push({ size, allocated, used });
    }
    return acc;
  }, {});

  return Object.entries(perBqn).reduce((acc, [bqn, values]) => {
    const newValue = calcFreeAndUsed(values.reduce(sumUpPools));
    acc.push({ ...newValue, bqnId: bqn });
    return acc;
  }, []);
};

const parseLines = (input, translations={}, units) => {
  const lines = input.length === 0 ? [] : input.trim().split("\n");
  if (lines.length === 0) {
    return null;
  }
  const [head, ...rows] = lines;
  const keys = parseKeys(head.trim(" "), translations);
  const skipped = keys.filter(
    (key) => Object.values(translations).includes(key) === false
  );
  return rows.map(parseFields(keys, skipped, units));
}

const calcDPDKFreeAndUsedInGBs = ({ total, used }, precision=3) => 
  ({
    free: fixedPrecision((total - used) * unitScale('M'), precision),
    used: fixedPrecision(used * unitScale('M'), precision),
  });

const sumUpDPDKPools = ({ total = 0, used = 0 }, row) =>
  ({
    total: total + row.total,
    used: used + row.used,
  });

export const parseDPDKMemoryUsage = (input, mode) => {
  const translations = {
    "SIZE-USED-MB": "used",
    "SIZE-TOTAL-MB": "total",
    "BQN": "bqnId",
  };
  const values = parseLines(input, translations);
  return (mode === "standalone")
    ? calcDPDKFreeAndUsedInGBs(values.reduce(sumUpDPDKPools))
    : Object.entries(
        groupBy('bqnId', values, {defaults: 0})
      ).reduce((acc, [bqnId, values]) => {
        const valuesSum = calcDPDKFreeAndUsedInGBs(values.reduce(sumUpDPDKPools));
        acc.push({ ...valuesSum, bqnId: bqnId !== 0 && Number(bqnId).toString() });
        return acc;
      }, []);
};
